import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Encabezado, Pestanas } from '../navegacion/Marco';
import {
  Aviso,
  Boton,
  Cajon,
  Cargando,
  Desplazable,
  ErrorEnPantalla,
  Insignia,
  Panel,
  Selector,
  Vacio,
  clases,
} from '../comun/componentes';
import { useAccion, useDatos } from '../comun/useDatos';
import { actualizar } from '../api/cliente';
import { fechaYHora, legible, porcentaje } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';
import type {
  AccesoSensibleVista,
  AuditoriaVista,
  ConvenioVista,
  PermisoCatalogo,
  UsuarioDeClinica,
} from '../api/tipos';

const SECCIONES = [
  { valor: 'usuarios', rotulo: 'Usuarios y permisos' },
  { valor: 'convenios', rotulo: 'Convenios' },
  { valor: 'auditoria', rotulo: 'Auditoría' },
  { valor: 'accesos', rotulo: 'Accesos a datos' },
];

const ROLES = ['Titular', 'Administrador', 'Odontologo', 'Recepcion', 'Asistente', 'Contador'];

/** Administración: quién puede qué, con quién se tiene convenio y qué se hizo en el sistema. */
export function Administracion() {
  const { seccion = 'usuarios' } = useParams();
  const navegar = useNavigate();

  return (
    <>
      <Encabezado
        titulo="Administración"
        detalle="Permisos, convenios y el rastro de todo lo que pasa en el sistema."
      />

      <Pestanas
        actual={seccion}
        opciones={SECCIONES}
        alCambiar={(valor) => navegar(`/administracion/${valor}`)}
      />

      {seccion === 'usuarios' && <Usuarios />}
      {seccion === 'convenios' && <Convenios />}
      {seccion === 'auditoria' && <Auditoria />}
      {seccion === 'accesos' && <Accesos />}
    </>
  );
}

function Usuarios() {
  const { usuario: yo, puede } = useSesion();
  const usuarios = useDatos<UsuarioDeClinica[]>('/api/v1/permisos/usuarios');
  const catalogo = useDatos<PermisoCatalogo[]>(
    puede('rol.administrar') ? '/api/v1/permisos/catalogo' : null,
  );
  const [editando, setEditando] = useState<UsuarioDeClinica | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  if (usuarios.cargando) return <Cargando que="el equipo" />;
  if (usuarios.error) return <ErrorEnPantalla mensaje={usuarios.error} reintentar={usuarios.recargar} />;

  return (
    <>
      {aviso && (
        <div className="mb-4">
          <Aviso tono="exito" alCerrar={() => setAviso(null)}>
            {aviso}
          </Aviso>
        </div>
      )}

      <Panel titulo="Equipo de la clínica" descripcion="El rol define el piso; las excepciones lo ajustan">
        <Desplazable>
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col">Usuario</th>
                <th scope="col">Rol</th>
                <th scope="col">Excepciones</th>
                <th scope="col" className="text-right">Permisos</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {(usuarios.datos ?? []).map((persona) => (
                <tr key={persona.usuarioId} className={clases(!persona.activa && 'opacity-50')}>
                  <td>
                    <span className="block font-medium text-tinta-900">{persona.nombre}</span>
                    <span className="block text-dato text-tinta-400">{persona.email}</span>
                  </td>
                  <td>
                    <Insignia tono="marca">{legible(persona.rol)}</Insignia>
                  </td>
                  <td>
                    {persona.overrides.length === 0 ? (
                      <span className="text-dato text-tinta-400">ninguna</span>
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {persona.overrides.map((over) => (
                          <Insignia key={over.codigo} tono={over.concede ? 'exito' : 'alerta'}>
                            {over.concede ? '+' : '−'} {over.codigo}
                          </Insignia>
                        ))}
                      </span>
                    )}
                  </td>
                  <td className="cifra text-right text-tinta-500">{persona.permisosEfectivos.length}</td>
                  <td className="text-right">
                    {puede('rol.administrar') && persona.usuarioId !== yo?.usuarioId && (
                      <Boton onClick={() => setEditando(persona)}>Editar</Boton>
                    )}
                    {persona.usuarioId === yo?.usuarioId && (
                      <span className="text-dato text-tinta-400">sos vos</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Desplazable>
      </Panel>

      <EditarPermisos
        persona={editando}
        catalogo={catalogo.datos ?? []}
        alCerrar={() => setEditando(null)}
        alGuardar={(nombre) => {
          setEditando(null);
          setAviso(`Permisos de ${nombre} actualizados.`);
          usuarios.recargar();
        }}
      />
    </>
  );
}

function EditarPermisos({
  persona,
  catalogo,
  alCerrar,
  alGuardar,
}: {
  persona: UsuarioDeClinica | null;
  catalogo: PermisoCatalogo[];
  alCerrar: () => void;
  alGuardar: (nombre: string) => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const [rol, setRol] = useState('');
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());

  // Al abrir el panel se toma el estado actual del usuario elegido.
  const clave = persona?.usuarioId ?? '';
  useMemo(() => {
    if (persona) {
      setRol(persona.rol);
      setOverrides(new Map(persona.overrides.map((o) => [o.codigo, o.concede])));
    }
  }, [clave]);

  const porModulo = useMemo(() => {
    const mapa = new Map<string, PermisoCatalogo[]>();

    for (const permiso of catalogo) {
      const lista = mapa.get(permiso.modulo) ?? [];
      lista.push(permiso);
      mapa.set(permiso.modulo, lista);
    }

    return mapa;
  }, [catalogo]);

  const guardar = async () => {
    if (!persona) return;

    const hecho = await correr(() =>
      actualizar(`/api/v1/permisos/usuarios/${persona.usuarioId}`, {
        rol,
        overrides: [...overrides.entries()].map(([codigo, concede]) => ({ codigo, concede })),
      }),
    );

    if (hecho !== null) {
      alGuardar(persona.nombre);
    }
  };

  const alternar = (codigo: string) => {
    const copia = new Map(overrides);

    // Tres estados en un solo control: sin excepción → concedido → denegado → sin excepción.
    if (!copia.has(codigo)) {
      copia.set(codigo, true);
    } else if (copia.get(codigo)) {
      copia.set(codigo, false);
    } else {
      copia.delete(codigo);
    }

    setOverrides(copia);
  };

  return (
    <Cajon
      abierto={persona != null}
      titulo={persona?.nombre ?? ''}
      alCerrar={alCerrar}
      pie={
        <div className="flex gap-2">
          <Boton variante="primario" ancho cargando={ejecutando} onClick={() => void guardar()}>
            Guardar permisos
          </Boton>
          <Boton onClick={alCerrar}>Cancelar</Boton>
        </div>
      }
    >
      <div className="space-y-4">
        <Selector etiqueta="Rol base" value={rol} onChange={(e) => setRol(e.target.value)}>
          {ROLES.map((opcion) => (
            <option key={opcion} value={opcion}>
              {legible(opcion)}
            </option>
          ))}
        </Selector>

        <p className="rounded-lg bg-papel-100 px-3 py-2 text-dato text-tinta-600">
          Las excepciones se aplican por encima del rol. Denegar gana siempre, incluso sobre el rol
          de titular.
        </p>

        {[...porModulo.entries()].map(([modulo, permisos]) => (
          <div key={modulo}>
            <p className="etiqueta-campo mb-2">{legible(modulo)}</p>
            <ul className="space-y-1">
              {permisos.map((permiso) => {
                const excepcion = overrides.get(permiso.codigo);
                const delRol = permiso.enRolBase.includes(rol);

                return (
                  <li key={permiso.codigo}>
                    <button
                      type="button"
                      onClick={() => alternar(permiso.codigo)}
                      className="flex min-h-tactil w-full items-center justify-between gap-3 rounded-lg px-2 text-left hover:bg-papel-100"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm text-tinta-800">{legible(permiso.accion)}</span>
                        <span className="cifra block text-dato text-tinta-400">{permiso.codigo}</span>
                      </span>
                      {excepcion === true ? (
                        <Insignia tono="exito">concedido</Insignia>
                      ) : excepcion === false ? (
                        <Insignia tono="alerta">denegado</Insignia>
                      ) : (
                        <Insignia tono={delRol ? 'marca' : 'neutro'}>
                          {delRol ? 'por el rol' : 'sin permiso'}
                        </Insignia>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}

function Convenios() {
  const { datos, cargando, error, recargar } = useDatos<ConvenioVista[]>('/api/v1/convenios');
  const [detalle, setDetalle] = useState<ConvenioVista | null>(null);

  if (cargando) return <Cargando que="los convenios" />;
  if (error) return <ErrorEnPantalla mensaje={error} reintentar={recargar} />;

  return (
    <>
      <Panel titulo="Convenios" descripcion="Empresas, cooperativas, prepagas y seguros">
        {datos?.length === 0 && (
          <Vacio
            titulo="Sin convenios"
            detalle="Cargá el primero para presupuestar con su tarifario y su cobertura."
          />
        )}

        {datos && datos.length > 0 && (
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Convenio</th>
                  <th scope="col">Tipo</th>
                  <th scope="col" className="text-right">Cobertura general</th>
                  <th scope="col">Autorización</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {datos.map((convenio) => (
                  <tr key={convenio.id}>
                    <td>
                      <span className="block font-medium text-tinta-900">{convenio.nombre}</span>
                      {convenio.razonSocial && (
                        <span className="block text-dato text-tinta-400">{convenio.razonSocial}</span>
                      )}
                    </td>
                    <td>
                      <Insignia>{legible(convenio.tipo)}</Insignia>
                    </td>
                    <td className="cifra text-right">
                      {porcentaje(convenio.coberturaGeneralPorcentaje, 0)}
                    </td>
                    <td>
                      {convenio.requiereAutorizacion ? (
                        <Insignia tono="aviso">exige previa</Insignia>
                      ) : (
                        <span className="text-dato text-tinta-400">no exige</span>
                      )}
                    </td>
                    <td className="text-right">
                      <Boton onClick={() => setDetalle(convenio)}>Coberturas</Boton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        )}
      </Panel>

      <Cajon
        abierto={detalle != null}
        titulo={detalle?.nombre ?? ''}
        alCerrar={() => setDetalle(null)}
      >
        {detalle && (
          <div className="space-y-4">
            <dl className="grid grid-cols-2 gap-3">
              <div>
                <dt className="etiqueta-campo">Tarifario</dt>
                <dd className="mt-0.5 text-sm">{detalle.tarifario ?? 'el particular'}</dd>
              </div>
              <div>
                <dt className="etiqueta-campo">Contacto</dt>
                <dd className="mt-0.5 text-sm">{detalle.contacto ?? '—'}</dd>
              </div>
            </dl>

            <div>
              <p className="etiqueta-campo mb-2">Coberturas por prestación</p>
              {detalle.coberturas.length === 0 ? (
                <p className="text-dato text-tinta-500">
                  Todo se cubre al {detalle.coberturaGeneralPorcentaje} % general.
                </p>
              ) : (
                <Desplazable>
                  <table className="tabla">
                    <thead>
                      <tr>
                        <th scope="col">Prestación</th>
                        <th scope="col" className="text-right">Cubre</th>
                        <th scope="col" className="text-right">Tope</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalle.coberturas.map((cobertura) => (
                        <tr key={cobertura.prestacionId}>
                          <td>{cobertura.prestacion}</td>
                          <td className="cifra text-right">{porcentaje(cobertura.porcentaje, 0)}</td>
                          <td className="cifra text-right">
                            {cobertura.topeGs ? cobertura.topeGs.toLocaleString('es-PY') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Desplazable>
              )}
            </div>
          </div>
        )}
      </Cajon>
    </>
  );
}

function Auditoria() {
  const [entidad, setEntidad] = useState('');
  const consulta = entidad ? `&entidad=${encodeURIComponent(entidad)}` : '';
  const { datos, cargando, error, recargar } = useDatos<AuditoriaVista[]>(
    `/api/v1/auditoria?limite=100${consulta}`,
    [entidad],
  );

  const entidades = useMemo(
    () => [...new Set((datos ?? []).map((a) => a.entidad))].sort(),
    [datos],
  );

  return (
    <Panel titulo="Auditoría de escrituras" descripcion="Quién cambió qué, y con qué valores">
      <Selector
        etiqueta="Entidad"
        value={entidad}
        onChange={(e) => setEntidad(e.target.value)}
        className="mb-3"
      >
        <option value="">Todas</option>
        {entidades.map((nombre) => (
          <option key={nombre} value={nombre}>
            {nombre}
          </option>
        ))}
      </Selector>

      {cargando && <Cargando que="la auditoría" />}
      {error && <ErrorEnPantalla mensaje={error} reintentar={recargar} />}
      {datos?.length === 0 && <Vacio titulo="Sin movimientos en el período" />}

      {datos && datos.length > 0 && (
        <Desplazable>
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col">Cuándo</th>
                <th scope="col">Quién</th>
                <th scope="col">Qué</th>
                <th scope="col">Acción</th>
              </tr>
            </thead>
            <tbody>
              {datos.map((asiento) => (
                <tr key={asiento.id}>
                  <td className="cifra whitespace-nowrap text-dato">
                    {fechaYHora(asiento.ocurridoEn)}
                  </td>
                  <td className="text-dato">{asiento.usuario}</td>
                  <td>
                    <span className="block text-sm">{asiento.entidad}</span>
                    <span className="cifra block text-dato text-tinta-400">{asiento.entidadId}</span>
                  </td>
                  <td>
                    <Insignia
                      tono={
                        asiento.accion === 'Alta'
                          ? 'exito'
                          : asiento.accion === 'Baja'
                            ? 'alerta'
                            : 'aviso'
                      }
                    >
                      {legible(asiento.accion)}
                    </Insignia>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Desplazable>
      )}
    </Panel>
  );
}

function Accesos() {
  const { datos, cargando, error, recargar } = useDatos<AccesoSensibleVista[]>(
    '/api/v1/auditoria/accesos?limite=100',
  );

  return (
    <Panel
      titulo="Accesos a datos de salud"
      descripcion="Quién abrió qué ficha, cuándo y desde dónde — Ley N° 7593/2025"
    >
      {cargando && <Cargando que="el log de accesos" />}
      {error && <ErrorEnPantalla mensaje={error} reintentar={recargar} />}
      {datos?.length === 0 && (
        <Vacio
          titulo="Sin accesos registrados en el período"
          detalle="Cada vez que alguien abra una historia clínica va a quedar registrado acá."
        />
      )}

      {datos && datos.length > 0 && (
        <Desplazable>
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col">Cuándo</th>
                <th scope="col">Quién miró</th>
                <th scope="col">Qué abrió</th>
                <th scope="col">Paciente</th>
                <th scope="col">Desde</th>
              </tr>
            </thead>
            <tbody>
              {datos.map((acceso) => (
                <tr key={acceso.id}>
                  <td className="cifra whitespace-nowrap text-dato">
                    {fechaYHora(acceso.ocurridoEn)}
                  </td>
                  <td className="text-dato">{acceso.usuario}</td>
                  <td>
                    <Insignia tono="marca">{legible(acceso.recurso)}</Insignia>
                    {acceso.alcance > 1 && (
                      <span className="ml-2 text-dato text-tinta-400">{acceso.alcance} pacientes</span>
                    )}
                  </td>
                  <td className="text-dato">{acceso.paciente ?? '—'}</td>
                  <td className="cifra text-dato text-tinta-400">{acceso.direccionIp ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Desplazable>
      )}
    </Panel>
  );
}
